import { tokenProvider } from './auth/token-provider.js';
import { EventEmitter } from '../utils/event-emitter.js';
import { getVersionedApiUrl, isLocalDev } from '../config/api-config.js';

export class BaseService {
  constructor() {
    this.basePath = '';
    
    // Use centralized API configuration
    let apiVersion = 'v1';
    try {
      // eslint-disable-next-line no-undef
      if (typeof __API_VERSION__ !== 'undefined' && __API_VERSION__) {
        // eslint-disable-next-line no-undef
        apiVersion = __API_VERSION__;
      } else if (!isLocalDev()) {
        apiVersion = 'v1';
      }
    } catch {}
    this.baseUrl = getVersionedApiUrl(apiVersion);
    
    // Only log API URL once per session to reduce console noise
    if (!window._KABLEWY_API_LOGGED) {
      console.log(`[BaseService] Using API URL: ${this.baseUrl}`);
      window._KABLEWY_API_LOGGED = true;
    }
    this.events = new EventEmitter();
    
    // Only log full URL in debug mode
    if (window.DEBUG_MODE) {
      console.log(`[BaseService] Full API URL: ${this.baseUrl}`);
    }
    
    // Setup network connectivity monitoring
    this._setupConnectivityMonitoring();
  }

  /**
   * Setup listeners for network connectivity changes
   * @private
   */
  _setupConnectivityMonitoring() {
    // Track network state
    this.isOnline = navigator.onLine;
    
    // Update state and emit events on connection changes
    window.addEventListener('online', () => {
      console.log('[BaseService] Network connection restored');
      this.isOnline = true;
      this.emit('network:online');
    });
    
    window.addEventListener('offline', () => {
      console.log('[BaseService] Network connection lost');
      this.isOnline = false;
      this.emit('network:offline');
    });
  }
  
  /**
   * Check if the app currently has connectivity
   * @returns {boolean}
   */
  isNetworkConnected() {
    return this.isOnline;
  }

  // Standard event handlers
  on(event, callback) {
    return this.events.on(event, callback);
  }

  off(event, callback) {
    return this.events.off(event, callback);
  }

  emit(event, data) {
    return this.events.emit(event, data);
  }

  getSessionInfo() {
    const sessionData = tokenProvider.getSessionData();
    if (!sessionData?.orgId || !sessionData?.userData?.id) {
      throw new Error('Missing required session data');
    }
    return { orgId: sessionData.orgId, userId: sessionData.userData.id };
  }

  handleError(error, context = {}) {
    const errorEvent = {
      error,
      context,
      timestamp: new Date().toISOString(),
      service: this.constructor.name
    };
    this.emit('error', errorEvent);
    return errorEvent;
  }

  async safeRequest(method, path, data) {
    try {
      const response = await this[method](path, data);
      return response;
    } catch (error) {
      this.handleError(error, { method, path, data });
      throw error;
    }
  }

  async search(query, options = {}) {
    const { orgId, userId } = this.getSessionInfo();
    return this.safeRequest('post', `/search`, {
      orgId,
      userId,
      query,
      options
    });
  }

  setBaseUrl(url) {
    this.baseUrl = url;
  }

  async getHeaders() {
    const token = tokenProvider.getToken();
    if (!token) {
      throw new Error('No session token found');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  async get(path, params = {}, options = {}) {
    const rawUrl = `${this.baseUrl}${this.basePath}${path}`;
    const url = new URL(rawUrl, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });

    const isSilent = params.silent;
    if (!isSilent) {
      console.log(`[BaseService] GET ${url.pathname}`);
    }
    
    try {
      const authHeaders = await this.getHeaders();
      const headers = { ...authHeaders, ...(options.headers || {}) };
      if (window.DEBUG_MODE && !isSilent) {
        console.log(`[BaseService] Request headers:`, headers);
      }
      
      const fetchOpts = {
        method: 'GET',
        headers: headers
      };
      if (options && options.signal) {
        fetchOpts.signal = options.signal;
      }
      const response = await fetch(url.toString(), fetchOpts);

      if (options && options.returnResponse) {
        if (response.ok || (options.allowNotModified && response.status === 304)) {
          return response;
        }
      }

      if (window.DEBUG_MODE && !isSilent) {
        console.log(`[BaseService] Response status:`, response.status, response.statusText);
      }
  
      if (!response.ok) {
        const errorText = await response.text();
        
        // Only log error responses that aren't 404s to reduce noise
        if (response.status !== 404) {
          console.error(`[BaseService] Error response:`, errorText);
        }
        
        try {
          const error = JSON.parse(errorText);
          throw new Error(error.message || 'Request failed');
        } catch (e) {
          throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
        }
      }
      
      // Check if there's actual content before trying to parse JSON
      const text = await response.text();
      console.log(`[BaseService] Raw response text:`, text);
      
      if (!text || text.trim() === '') {
        console.warn(`[BaseService] Empty response body`);
        return {}; // Return empty object instead of failing
      }
      
      try {
        const data = JSON.parse(text);
        console.log(`[BaseService] Parsed response data:`, data);
        return data;
      } catch (error) {
        console.error(`[BaseService] JSON parsing error:`, error);
        console.error(`[BaseService] Failed to parse response text:`, text);
        throw new Error('Invalid JSON response from server');
      }
    } catch (error) {
      if (error && (error.name === 'AbortError' || /signal is aborted/i.test(error.message || ''))) {
        // Suppress noisy abort logs during debounced searches
        return Promise.reject(error);
      }
      console.error(`[BaseService] Request error:`, error);
      throw error;
    }
  }

  async post(path, data = {}, options = {}) {
    const url = `${this.baseUrl}${this.basePath}${path}`;
    console.log(`[BaseService] Making POST request to: ${url}`, data);
    
    try {
      const authHeaders = await this.getHeaders();
      const headers = { ...authHeaders, ...(options.headers || {}) };
      console.log(`[BaseService] Request headers:`, headers);
      
      const isMultipart = (typeof FormData !== 'undefined' && data instanceof FormData) || options.isMultipart === true;
      if (isMultipart && headers['Content-Type']) {
        // Let the browser set the multipart boundary automatically
        try { delete headers['Content-Type']; } catch {}
      }

      const fetchOptions = {
        method: 'POST',
        headers: headers,
        body: isMultipart ? data : JSON.stringify(data)
      };
      if (options && options.signal) {
        fetchOptions.signal = options.signal;
      }
      const response = await fetch(url, fetchOptions);
      if (options && options.returnResponse) {
        if (response.ok || (options.allowNotModified && response.status === 304)) {
          return response;
        }
      }
      
      console.log(`[BaseService] Response status:`, response.status, response.statusText);
      
      if (!response.ok) {
        let errorText;
        try {
          errorText = await response.text();
          console.error(`[BaseService] Error response:`, errorText);
          
          try {
            const error = JSON.parse(errorText);
            const errorObj = new Error(error.message || error.error || 'Request failed');
            errorObj.status = response.status;
            errorObj.statusCode = response.status;
            errorObj.details = error.details || error.reason || null;
            throw errorObj;
          } catch (e) {
            if (e instanceof Error && e.status) {
              throw e; // Re-throw if it's already our formatted error
            }
            const errorObj = new Error(`Request failed with status ${response.status}: ${response.statusText}`);
            errorObj.status = response.status;
            errorObj.statusCode = response.status;
            throw errorObj;
          }
        } catch (e) {
          if (e instanceof SyntaxError) {
            const errorObj = new Error(`Request failed with status ${response.status}: ${response.statusText}`);
            errorObj.status = response.status;
            errorObj.statusCode = response.status;
            throw errorObj;
          }
          throw e;
        }
      }
      
      const responseData = await response.json().catch(() => ({}));
      console.log(`[BaseService] Response data:`, responseData);
      return responseData;
    } catch (error) {
      if (error && (error.name === 'AbortError' || /signal is aborted/i.test(error.message || ''))) {
        return Promise.reject(error);
      }
      console.error(`[BaseService] Request error:`, error);
      throw error;
    }
  }

  async put(path, data = {}, options = {}) {
    const url = `${this.baseUrl}${this.basePath}${path}`;
    console.log(`[BaseService] Making PUT request to: ${url}`, data);
    
    try {
      const authHeaders = await this.getHeaders();
      const headers = { ...authHeaders, ...(options.headers || {}) };
      console.log(`[BaseService] Request headers:`, headers);
      
      const fetchOptions = {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify(data)
      };
      if (options && options.signal) {
        fetchOptions.signal = options.signal;
      }
      const response = await fetch(url, fetchOptions);
      if (options && options.returnResponse) {
        if (response.ok || (options.allowNotModified && response.status === 304)) {
          return response;
        }
      }
      
      console.log(`[BaseService] Response status:`, response.status, response.statusText);
      
      if (!response.ok) {
        let errorText;
        try {
          errorText = await response.text();
          console.error(`[BaseService] Error response:`, errorText);
          
          try {
            const error = JSON.parse(errorText);
            throw new Error(error.message || error.error || 'Request failed');
          } catch (e) {
            throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
          }
        } catch (e) {
          if (e instanceof SyntaxError) {
            throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
          }
          throw e;
        }
      }
      
      const responseData = await response.json().catch(() => ({}));
      console.log(`[BaseService] Response data:`, responseData);
      return responseData;
    } catch (error) {
      if (error && (error.name === 'AbortError' || /signal is aborted/i.test(error.message || ''))) {
        return Promise.reject(error);
      }
      console.error(`[BaseService] Request error:`, error);
      throw error;
    }
  }

  async patch(path, data = {}) {
    const response = await fetch(`${this.baseUrl}${this.basePath}${path}`, {
      method: 'PATCH',
      headers: await this.getHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Request failed');
    }

    return response.json();
  }
  
  async delete(path, data = null, options = {}) {
    const url = `${this.baseUrl}${this.basePath}${path}`;
    console.log(`[BaseService] Making DELETE request to: ${url}`, data);
    
    try {
      const authHeaders = await this.getHeaders();
      const headers = { ...authHeaders, ...(options.headers || {}) };
      console.log(`[BaseService] Request headers:`, headers);
      
      const requestOptions = {
        method: 'DELETE',
        headers: headers
      };
      
      if (data) {
        requestOptions.body = JSON.stringify(data);
      }
      if (options && options.signal) {
        requestOptions.signal = options.signal;
      }
      
      const response = await fetch(url, requestOptions);
      
      console.log(`[BaseService] Response status:`, response.status, response.statusText);
      
      if (!response.ok) {
        let errorText;
        try {
          errorText = await response.text();
          console.error(`[BaseService] Error response:`, errorText);
          
          try {
            const error = JSON.parse(errorText);
            throw new Error(error.message || error.error || 'Request failed');
          } catch (e) {
            throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
          }
        } catch (e) {
          if (e instanceof SyntaxError) {
            throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
          }
          throw e;
        }
      }
      
      // Check if there's content to parse
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const responseData = await response.json().catch(() => ({}));
        console.log(`[BaseService] Response data:`, responseData);
        return responseData;
      }
      
      console.log(`[BaseService] Empty response or non-JSON content`);
      return null;
    } catch (error) {
      console.error(`[BaseService] Request error:`, error);
      throw error;
    }
  }

  async fetchApi(url, options = {}, errorMessage = 'Request failed') {
    try {
      // Merge headers with auth headers
      const authHeaders = await this.getHeaders();
      const headers = { ...authHeaders, ...(options.headers || {}) };
      const requestOptions = { ...options, headers };

      // Log only the URL and method, not the full payload to prevent UI thread blocking
      console.log(`[BaseService] ${requestOptions.method || 'GET'} ${url.split('?')[0]}`);
      
      // Preserve timeout in options for test expectations; do not override signal when not provided
      const timeoutMs = options.timeout || 30000; // 30 second default timeout (metadata only)
      
      try {
        const response = await fetch(url, {
          ...requestOptions
        });
        
        // No internal timeout aborting; rely on caller/network behavior
        
        if (!response.ok) {
          const text = await response.text().catch(() => '');
          const msg = `${errorMessage}: ${response.status} ${response.statusText}${text ? ` - ${text}` : ''}`;
          throw new Error(msg);
        }

        // Some endpoints return empty bodies (e.g., 204)
        const text = await response.text();
        if (!text) return {};
        
        try {
          return JSON.parse(text);
        } catch {
          throw new Error('Invalid JSON response from server');
        }
      } finally {
        // nothing
      }
    } catch (error) {
      // Provide more helpful messages for network errors
      const suppress404 = !!(options && (options.silent || options.suppress404 || options.suppressErrorLog));
      const is404 = typeof error?.message === 'string' && /\b404\b/.test(error.message);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('[BaseService] Network error - check connection', error.message);
        throw new Error(`Network connection error: ${error.message}`);
      } else if (error.name === 'AbortError') {
        console.error('[BaseService] Request timed out or was aborted');
        throw new Error('Request timed out. Please check your connection and try again.');
      } else {
        // Downgrade logging for expected 404s or when caller requests silence
        if (is404 && suppress404) {
          console.warn('[BaseService] Suppressed 404:', error.message);
        } else if (!(options && options.silent)) {
          console.error('[BaseService] fetchApi error:', error.message);
        }
        throw error;
      }
    }
  }
} 